'use client';

import { useCallback, useState } from 'react';

import { SellerFlowAddressAutocomplete } from '@/components/forms/seller-flow-address-autocomplete';

export type StickyAddressWidgetEntry = {
  title?: string;
  subtitle?: string;
  placeholder?: string;
};

type WidgetState = 'visible' | 'minimized' | 'expanded';

export function StickyAddressWidget({ entry }: { entry: StickyAddressWidgetEntry }) {
  const [state, setState] = useState<WidgetState>('visible');
  const title = entry.title ?? 'What’s your home worth?';
  const subtitle = entry.subtitle ?? 'Get a no-obligation cash offer from Opendoor.';
  const placeholder = entry.placeholder ?? 'Enter your home address';

  const onCardClick = useCallback(() => {
    if (typeof window !== 'undefined' && window.innerWidth <= 767) {
      if (state === 'visible') setState('expanded');
      if (state === 'minimized') setState('visible');
    }
  }, [state]);

  const close = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setState('visible');
  }, []);

  const toggleMinimized = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setState((s) => (s === 'minimized' ? 'visible' : 'minimized'));
  }, []);

  return (
    <>
      {state === 'expanded' && (
        <div
          aria-hidden
          onClick={close}
          className="fixed inset-0 z-[99] bg-canvas-dark/50 md:hidden"
        />
      )}

      <div
        onClick={onCardClick}
        data-state={state}
        className={[
          'lg:sticky lg:top-24 lg:z-10 lg:cursor-default',
          'fixed inset-x-0 bottom-0 z-[100] cursor-pointer px-4 pb-4',
          state === 'expanded'
            ? 'top-20 bottom-auto left-1/2 right-auto w-[calc(100%-32px)] max-w-md -translate-x-1/2 cursor-default'
            : '',
          'lg:relative lg:inset-auto lg:translate-x-0 lg:px-0 lg:pb-0',
        ].join(' ')}
      >
        <div className="rounded-3xl bg-canvas-deep p-6 shadow-[0_8px_24px_rgba(0,0,0,0.04)] sm:p-8 lg:p-8">
          {state === 'expanded' && (
            <div className="mb-2 flex justify-end md:hidden">
              <button
                onClick={close}
                aria-label="Close"
                className="flex h-11 w-11 shrink-0 items-center justify-center text-2xl leading-none text-ink-faint"
              >
                ×
              </button>
            </div>
          )}

          <div className="flex items-start justify-between gap-3">
            <h2 className="font-medium leading-tight text-ink text-subheader-md sm:text-subheader-lg">
              {title}
            </h2>
            {state !== 'expanded' && (
              <button
                aria-label={state === 'minimized' ? 'Expand widget' : 'Minimize widget'}
                onClick={toggleMinimized}
                className="-mr-2 flex h-11 w-11 shrink-0 items-center justify-center text-ink-faint md:hidden"
              >
                {state === 'minimized' ? '▼' : '▲'}
              </button>
            )}
          </div>

          <div className={state === 'minimized' ? 'hidden md:block' : 'mt-4'}>
            {subtitle && <p className="text-ink-muted text-body-md">{subtitle}</p>}
            <div className="mt-5">
              <SellerFlowAddressAutocomplete
                placeholder={placeholder}
                loadingLabel="Confirming your address…"
                loadingClassName="text-ink-muted text-body-md"
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
